// Angular
import {HttpClient, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import { Injectable } from '@angular/core';
// Other
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

export enum SYSTEM_APP {
	INTELLIGENCE = 'intelligence',
	APPRAISAL = 'appraisal',
	IDENTITY_SYSTEM = 'identity-system',
	GLOBAL_CONFIGURATION = 'global-configuration'
}

@Injectable({
	providedIn: 'root'
})
export class ApiService {
	private options = { headers: new HttpHeaders().set('Content-Type', 'application/json') };
	// Every call goes to the Proxy API, so the URL it is always /api/
	private BASE_URL = 'api';

	private currentApp = '';

	constructor(private httpClient: HttpClient) {
		// We default to Intelligence
		this.setSystemApp(SYSTEM_APP.INTELLIGENCE);
	}

	/**
	 * SET what is going to be the current app to use, defaulted to Intelligence
	 * @param systemApp
	 */
	public setSystemApp(systemApp: SYSTEM_APP | string): void {
		this.currentApp = '/' + systemApp;
	}

	/**
	 * Performs a GET api call.
	 * @param path: string
	 * @param params: HttpParams
	 * @param systemApi: boolean
	 */
	public get(
		path: string,
		params: HttpParams = new HttpParams(),
		systemPath = false,
		observe = false
	): Observable<any> {
		if (!observe) {
			return this.httpClient.get(this.getSystemPath(path, systemPath), {params}).pipe(catchError(this.formatErrors));
		}

		return this.httpClient
			.get<HttpResponse<any>>(this.getSystemPath(path, systemPath), {observe: 'response'})
			.pipe(catchError(this.formatErrors));
	}

	/**
	 * Performs a PUT api call.
	 * @param path: string
	 * @param body: any
	 * @param systemApi: boolean
	 */
	public put(path: string, body: any = {}, systemPath = false, options: any = null): Observable<any> {
		return this.httpClient
			.put(this.getSystemPath(path, systemPath), JSON.stringify(body), ((options !== null) ? options : this.options))
			.pipe(catchError(this.formatErrors));
	}

	/**
	 * Performs a POST api call.
	 * @param path: string
	 * @param body: any
	 * @param systemApi: boolean
	 */
	public post(path: string, body: any = {}, systemPath = false): Observable<any> {
		return this.httpClient
			.post(this.getSystemPath(path, systemPath), JSON.stringify(body), this.options)
			.pipe(catchError(this.formatErrors));
	}

	/**
	 * Performs a PUT Form Data api call.
	 * @param path: string
	 * @param formData: FormData
	 * @param systemApi: boolean
	 */
	public putFormData(
		path: string,
		formData: FormData,
		systemPath = false
	): Observable<any> {
		return this.httpClient
			.put(this.getSystemPath(path, systemPath), formData)
			.pipe(catchError(this.formatErrors));
	}

	/**
	 * Performs a POST Form Data api call.
	 * @param path: string
	 * @param formData: FormData
	 * @param systemApi: boolean
	 */
	public postFormData(
		path: string,
		formData: FormData,
		systemPath = false
	): Observable<any> {
		return this.httpClient
			.post(this.getSystemPath(path, systemPath), formData)
			.pipe(catchError(this.formatErrors));
	}

	/**
	 * Performs a DELETE api call.
	 * @param path: string
	 * @param systemApi: boolean
	 */
	public delete(path: string, systemPath = false): Observable<any> {
		return this.httpClient
			.delete(this.getSystemPath(path, systemPath))
			.pipe(catchError(this.formatErrors));
	}

	/**
	 * Format errors
	 * @param error: any
	 */
	public formatErrors(error: any): Observable<any> {
		return throwError(error.error);
	}

	/**
	 * Used to retrieve blob response from server api.
	 * @param path
	 * @param options
	 * @param systemApi: boolean
	 */
	public getBlob(path: string, options: any, systemPath = false): Observable<any> {
		return this.httpClient
			.get(this.getSystemPath(path, systemPath), options)
			.pipe(catchError(this.formatErrors));
	}

	/**
	 * Get the Final App
	 * @param path: string
	 * @param systemApi: boolean
	 */
	private getSystemPath(path: string, systemPath: boolean): string {
		if (systemPath) {
			// console.log(`${this.BASE_URL}${path}`);
			return `${this.BASE_URL}${path}`;
		}
		return `${this.BASE_URL}${this.currentApp}${path}`;
	}
}
